import React, { useEffect, useMemo, useState } from "react";
import { api, urls } from "../../api/api";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import PaginationBar from "../../components/Common/PaginationBar";

const Services = () => {
  document.title = "Services";

  // ✅ role check
  const rawUser = localStorage.getItem("user") || localStorage.getItem("authUser");
  let currentUser = {};
  try {
    currentUser = rawUser ? JSON.parse(rawUser) : {};
  } catch {
    currentUser = {};
  }
  const isAdmin = currentUser?.role === "admin";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  // search + pagination
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // modal (admin only)
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: "",
    duration_min: 30,
    description: "",
  });

  const toggle = () => {
    setIsOpen((v) => !v);
    if (isOpen) {
      setEditingId(null);
      setForm({ name: "", duration_min: 30, description: "" });
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(urls.services);
      setServices(Array.isArray(r.data) ? r.data : []);
      setPage(1);
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur chargement services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ name: "", duration_min: 30, description: "" });
    setIsOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name || "",
      duration_min: s.duration_min || 30,
      description: s.description || "",
    });
    setIsOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return alert("Nom obligatoire");

    try {
      if (editingId) await api.put(`${urls.services}/${editingId}`, form);
      else await api.post(urls.services, form);

      toggle();
      await refresh();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join("\n") : "Erreur sauvegarde");
      alert(msg);
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Désactiver ce service ?")) return;
    try {
      await api.delete(`${urls.services}/${id}`);
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur suppression");
    }
  };

  // filter
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return services;
    return services.filter((x) => {
      const name = (x.name || "").toLowerCase();
      const desc = (x.description || "").toLowerCase();
      const dur = String(x.duration_min ?? "").toLowerCase();
      return name.includes(s) || desc.includes(s) || dur.includes(s);
    });
  }, [services, q]);

  // pagination
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div style={{ width: 420, maxWidth: "100%" }}>
                  <Input
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Recherche service: nom, durée, description..."
                  />
                </div>

                <div className="d-flex gap-2">
                  <Button color="secondary" onClick={refresh}>
                    Refresh
                  </Button>

                  {/* ✅ admin only */}
                  {isAdmin && (
                    <Button color="primary" onClick={openAdd}>
                      + Ajouter service
                    </Button>
                  )}
                </div>
              </div>

              <div className="table-responsive">
                <Table className="table align-middle table-nowrap">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Durée (min)</th>
                      <th>Description</th>
                      <th>Actif</th>
                      {isAdmin && <th style={{ width: 180 }}>Actions</th>}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={isAdmin ? 5 : 4}>Chargement...</td>
                      </tr>
                    ) : paginated.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 5 : 4}>Aucun service</td>
                      </tr>
                    ) : (
                      paginated.map((s) => (
                        <tr key={s.id}>
                          <td>{s.name}</td>
                          <td>{s.duration_min}</td>
                          <td>{s.description || "-"}</td>
                          <td>{s.is_active ? "Oui" : "Non"}</td>

                          {/* ✅ actions admin only */}
                          {isAdmin && (
                            <td>
                              <Button size="sm" color="info" className="me-2" onClick={() => openEdit(s)}>
                                Modifier
                              </Button>
                              <Button size="sm" color="danger" disabled={!s.is_active} onClick={() => deactivate(s.id)}>
                                Désactiver
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <PaginationBar
                page={safePage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ✅ Modal admin only */}
      {isAdmin && (
        <Modal isOpen={isOpen} toggle={toggle} centered>
          <ModalHeader toggle={toggle}>{editingId ? "Modifier service" : "Ajouter service"}</ModalHeader>

          <ModalBody>
            <Form>
              <FormGroup>
                <Label>Nom</Label>
                <Input name="name" value={form.name} onChange={onChange} />
              </FormGroup>

              <FormGroup>
                <Label>Durée (minutes)</Label>
                <Input type="number" name="duration_min" value={form.duration_min} onChange={onChange} />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <Input type="textarea" name="description" value={form.description} onChange={onChange} />
              </FormGroup>
            </Form>
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={toggle}>
              Fermer
            </Button>
            <Button color="primary" onClick={save}>
              Enregistrer
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default Services;
